import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TuneIcon from '@mui/icons-material/Tune';
import HandshakeIcon from '@mui/icons-material/Handshake';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ModalForm from './panel/dashboard/addModal/ModalForm';
import DI from './utility/DI';

const Navigators = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');
  const isDark = theme.palette.mode === 'dark';

  const [quickActionOpen, setQuickActionOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [formType, setFormType] = useState('shared_expense');

  const openModal = (type) => {
    setFormType(type);
    setModalOpen(true);
    setQuickActionOpen(false);
  };
  const closeModal = () => setModalOpen(false);

  const {
    di: { session },
  } = props;

  const hasHomes = session?.homes?.length > 0;

  // Determine active tab
  let activeTab = '/home';
  if (currentPath === '/home' || currentPath.startsWith('/home')) {
    activeTab = '/home';
  } else if (currentPath === '/panel/ai') {
    activeTab = '/panel/ai';
  } else if (
    currentPath.startsWith('/panel/manage') ||
    currentPath.startsWith('/panel/settings') ||
    currentPath.startsWith('/panel/friends')
  ) {
    activeTab = '/panel/manage';
  } else if (currentPath === '/panel' || currentPath === '/panel/') {
    activeTab = '/panel';
  }

  const handleNavClick = (path) => {
    if (path === '/home' && !hasHomes) {
      navigate('/panel/manage?section=homes');
      return;
    }
    navigate(path);
  };

  // Hide bottom navigation bar on dedicated full-screen pages like /panel/ai
  if (currentPath === '/panel/ai') {
    return null;
  }

  return (
    <>
      {/* Mobile: Floating Bottom Navigation Bar matching reference design */}
      {isMobile ? (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            right: 16,
            maxWidth: 440,
            mx: 'auto',
            height: 64,
            borderRadius: '32px',
            bgcolor: isDark ? 'rgba(15, 23, 42, 0.88)' : 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(16px)',
            boxShadow: isDark
              ? '0 12px 32px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.08)'
              : '0 12px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            px: 1.5,
            zIndex: 1300,
          }}
        >
          {/* 1. Home — goes to /home (Household Expenses) */}
          <IconButton
            onClick={() => handleNavClick('/home')}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              color: activeTab === '/home' ? 'primary.main' : 'text.secondary',
              p: 1,
              transition: 'all 0.2s',
            }}
          >
            <HomeIcon sx={{ fontSize: 22 }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.62rem',
                fontWeight: activeTab === '/home' ? 700 : 500,
                lineHeight: 1,
                mt: 0.3,
              }}
            >
              Home
            </Typography>
          </IconButton>

          {/* 2. Expenses — goes to /panel (Shared / Split Expenses Dashboard) */}
          <IconButton
            onClick={() => handleNavClick('/panel')}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              color: activeTab === '/panel' ? 'primary.main' : 'text.secondary',
              p: 1,
              transition: 'all 0.2s',
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 22 }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.62rem',
                fontWeight: activeTab === '/panel' ? 700 : 500,
                lineHeight: 1,
                mt: 0.3,
              }}
            >
              Expenses
            </Typography>
          </IconButton>

          {/* 3. Center FAB + two fan-out action buttons */}
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

            {/* Backdrop — closes menu on tap outside */}
            {quickActionOpen && (
              <Box
                onClick={() => setQuickActionOpen(false)}
                sx={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 1298,
                }}
              />
            )}

            {/* FAB: Home Expense — fans to upper-LEFT */}
            <Box
              onClick={() => openModal('home_expense')}
              sx={{
                position: 'absolute',
                top: 0, left: 0,
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#3b82f6',
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 1299,
                boxShadow: '0 4px 14px rgba(59,130,246,0.5)',
                opacity: quickActionOpen ? 1 : 0,
                pointerEvents: quickActionOpen ? 'auto' : 'none',
                transform: quickActionOpen
                  ? 'translate(50px, -68px) scale(1)'
                  : 'translate(0, 0) scale(0.6)',
                transition: 'all 0.25s cubic-bezier(0.34,1.56,0.64,1)',
              }}
            >
              <HomeIcon sx={{ fontSize: 20 }} />
              <Typography sx={{ fontSize: '0.52rem', fontWeight: 700, lineHeight: 1, mt: 0.2 }}>Home</Typography>
            </Box>

            {/* FAB: Split Expense — fans to upper-RIGHT */}
            <Box
              onClick={() => openModal('shared_expense')}
              sx={{
                position: 'absolute',
                top: 0, left: 0,
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#22c55e',
                color: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 1299,
                boxShadow: '0 4px 14px rgba(34,197,94,0.5)',
                opacity: quickActionOpen ? 1 : 0,
                pointerEvents: quickActionOpen ? 'auto' : 'none',
                transform: quickActionOpen
                  ? 'translate(-50px, -68px) scale(1)'
                  : 'translate(0, 0) scale(0.6)',
                transition: 'all 0.25s cubic-bezier(0.34,1.56,0.64,1)',
                transitionDelay: quickActionOpen ? '0.04s' : '0s',
              }}
            >
              <HandshakeIcon sx={{ fontSize: 20 }} />
              <Typography sx={{ fontSize: '0.52rem', fontWeight: 700, lineHeight: 1, mt: 0.2 }}>Split</Typography>
            </Box>

            {/* Main + button — rotates to × when open */}
            <Box
              onClick={() => setQuickActionOpen((prev) => !prev)}
              sx={{
                width: 48,
                height: 48,
                borderRadius: '24px',
                bgcolor: quickActionOpen
                  ? (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.08)')
                  : (isDark ? '#ffffff' : '#0f172a'),
                color: quickActionOpen
                  ? (isDark ? '#ffffff' : '#0f172a')
                  : (isDark ? '#0f172a' : '#ffffff'),
                border: quickActionOpen ? '2px solid' : 'none',
                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 1299,
                boxShadow: isDark
                  ? '0 6px 18px rgba(255,255,255,0.25)'
                  : '0 6px 18px rgba(15,23,42,0.35)',
                transition: 'all 0.2s ease',
                transform: quickActionOpen ? 'rotate(45deg)' : 'rotate(0deg)',
              }}
            >
              <AddIcon sx={{ fontSize: 26 }} />
            </Box>
          </Box>

          {/* 4. Ask AI (Sparkle) */}
          <IconButton
            onClick={() => handleNavClick('/panel/ai')}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              color: activeTab === '/panel/ai' ? 'primary.main' : 'text.secondary',
              p: 1,
              transition: 'all 0.2s',
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 22 }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.62rem',
                fontWeight: activeTab === '/panel/ai' ? 700 : 500,
                lineHeight: 1,
                mt: 0.3,
              }}
            >
              Ask AI
            </Typography>
          </IconButton>

          {/* 5. Profile / Manage */}
          <IconButton
            onClick={() => handleNavClick('/panel/manage')}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              color: activeTab === '/panel/manage' ? 'primary.main' : 'text.secondary',
              p: 1,
              transition: 'all 0.2s',
            }}
          >
            <TuneIcon sx={{ fontSize: 22 }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.62rem',
                fontWeight: activeTab === '/panel/manage' ? 700 : 500,
                lineHeight: 1,
                mt: 0.3,
              }}
            >
              Manage
            </Typography>
          </IconButton>
        </Box>
      ) : (
        /* Desktop: Clean Top Navigation Bar */
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => handleNavClick(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              bgcolor: isDark ? 'rgba(30, 41, 59, 0.5)' : '#ffffff',
              borderRadius: 3,
              p: 0.5,
              border: '1px solid',
              borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
            }}
          >
            <Tab
              id="nav-home-expenses"
              label="Home Expenses"
              value="/home"
              icon={<HomeIcon />}
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              id="nav-shared-expenses"
              label="Shared Expenses"
              value="/panel"
              icon={<ReceiptLongIcon />}
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              id="nav-add-friend"
              label="Friends"
              value="/panel/friends"
              icon={<GroupAddIcon />}
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              id="nav-ai"
              label="Ask AI"
              value="/panel/ai"
              icon={<AutoAwesomeIcon />}
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
            <Tab
              id="nav-manage"
              label="Manage"
              value="/panel/manage"
              icon={<TuneIcon />}
              iconPosition="start"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          </Tabs>
        </Box>
      )}

      {/* Expense Modal — controlled by nav FABs */}
      <ModalForm open={modalOpen} formType={formType} handleClose={closeModal} />

    </>
  );
};

export default DI(Navigators);